import React, { useEffect, useState } from 'react';
import { dealMgmt } from '../../services/dealManagementApi';
import { Card, Badge, Tabs } from '../../components/ui';
import { LoadingState, ErrorState, SaveIndicator } from '../../features/shared/PageStates';
import { RATING_BADGE } from '../../lib/statusMaps';
import { humanize } from '../../lib/format';

export function DealRegistersPage({ showToast }) {
  const [tab, setTab] = useState('End-Client Rating');
  const [endClients, setEndClients] = useState([]);
  const [directors, setDirectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [ecForm, setEcForm] = useState({ name: '', rating: 'not_classified', status_notes: '' });
  const [dirForm, setDirForm] = useState({ full_name: '', id_number: '', status: 'clear', status_notes: '' });

  const load = () => {
    setLoading(true);
    setError('');
    Promise.all([dealMgmt.endClients(), dealMgmt.directors()])
      .then(([ec, dir]) => {
        setEndClients(ec.items || ec.end_clients || []);
        setDirectors(dir.items || dir.directors || []);
      })
      .catch(e => {
        setError(e.message);
        showToast?.(e.message, 'error');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const addEndClient = async () => {
    if (!ecForm.name.trim() || busy) return;
    setBusy(true);
    setSaveStatus('saving');
    try {
      await dealMgmt.createEndClient(ecForm);
      setEcForm({ name: '', rating: 'not_classified', status_notes: '' });
      setSaveStatus('saved');
      showToast?.('End-client added');
      load();
      setTimeout(() => setSaveStatus('idle'), 1500);
    } catch (e) {
      setSaveStatus('error');
      showToast?.(e.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const addDirector = async () => {
    if (!dirForm.full_name.trim() || busy) return;
    setBusy(true);
    setSaveStatus('saving');
    try {
      await dealMgmt.createDirector(dirForm);
      setDirForm({ full_name: '', id_number: '', status: 'clear', status_notes: '' });
      setSaveStatus('saved');
      showToast?.('Director added');
      load();
      setTimeout(() => setSaveStatus('idle'), 1500);
    } catch (e) {
      setSaveStatus('error');
      showToast?.(e.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <LoadingState label="Loading registers…" />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px 0' }}>
        <Tabs tabs={['End-Client Rating', 'Director Register']} active={tab} onChange={setTab} />
        <SaveIndicator status={saveStatus} />
      </div>

      {tab === 'End-Client Rating' && (
        <div className="card-body">
          <div className="form-row fr3" style={{ marginBottom: 16 }}>
            <input className="field-input" placeholder="End-client name" value={ecForm.name} onChange={e => setEcForm(p => ({ ...p, name: e.target.value }))} />
            <select className="field-input" value={ecForm.rating} onChange={e => setEcForm(p => ({ ...p, rating: e.target.value }))}>
              <option value="green">Green</option>
              <option value="amber">Amber</option>
              <option value="red">Red</option>
              <option value="not_classified">Not Classified</option>
            </select>
            <button className="btn btn-p" disabled={busy} onClick={addEndClient}>{busy ? 'Saving…' : 'Add'}</button>
          </div>
          <table className="tbl">
            <thead><tr><th>Name</th><th>Rating</th><th>Notes</th></tr></thead>
            <tbody>
              {endClients.map(ec => (
                <tr key={ec.id}>
                  <td className="fw">{ec.name}</td>
                  <td><Badge type={RATING_BADGE[ec.rating] || 'neutral'}>{ec.rating}</Badge></td>
                  <td>{ec.status_notes || '—'}</td>
                </tr>
              ))}
              {endClients.length === 0 && (
                <tr><td colSpan={3} style={{ textAlign: 'center', color: 'var(--text3)' }}>No end-clients yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'Director Register' && (
        <div className="card-body">
          <p style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 14 }}>
            Manual register entries. Upload blacklist Excel on the deal form (New deal / Stage 1) when screening.
          </p>
          <div className="form-row fr3" style={{ marginBottom: 12 }}>
            <input className="field-input" placeholder="Full name" value={dirForm.full_name} onChange={e => setDirForm(p => ({ ...p, full_name: e.target.value }))} />
            <input className="field-input" placeholder="ID number" value={dirForm.id_number} onChange={e => setDirForm(p => ({ ...p, id_number: e.target.value }))} />
            <select className="field-input" value={dirForm.status} onChange={e => setDirForm(p => ({ ...p, status: e.target.value }))}>
              <option value="clear">Clear</option>
              <option value="watchlist">Watchlist</option>
              <option value="blacklisted">Blacklisted</option>
            </select>
          </div>
          <button className="btn btn-p" style={{ marginBottom: 12 }} disabled={busy} onClick={addDirector}>
            {busy ? 'Saving…' : 'Add director'}
          </button>
          <table className="tbl">
            <thead><tr><th>Name</th><th>ID</th><th>Status</th><th>Notes</th></tr></thead>
            <tbody>
              {directors.map(d => (
                <tr key={d.id}>
                  <td className="fw">{d.full_name}</td>
                  <td className="mono">{d.id_number || '—'}</td>
                  <td>
                    <Badge type={d.status === 'blacklisted' ? 'danger' : d.status === 'watchlist' ? 'warn' : 'success'}>
                      {humanize(d.status)}
                    </Badge>
                  </td>
                  <td>{d.status_notes || '—'}</td>
                </tr>
              ))}
              {directors.length === 0 && (
                <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--text3)' }}>No directors yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

export default DealRegistersPage;
